import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import SectionHeader from "../components/SectionHeader";
import { apiService } from "../services/api";
import logger from "../utils/logger";

const contactItems = [
  { icon: "📍", label: "Global Offices", value: "USA" },
  { icon: "📧", label: "Email", value: "contact@tekstationusa.com" },
  { icon: "📞", label: "Phone", value: "+1 (214) 973-3766" },
];

const serviceOptions = [
  "Cloud Engineering",
  "Data Engineering",
  "AI & Machine Learning",
  "Application Development",
  "Multiple / Not Sure",
];

const initialForm = { fname: "", lname: "", email: "", service: "", message: "" };
const initialErrors = { fname: "", lname: "", email: "", service: "", message: "" };

const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState(initialErrors);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [rateLimited, setRateLimited] = useState(false);
  const [retryAfter, setRetryAfter] = useState(0);
  const [cooldownTime, setCooldownTime] = useState(0);

  // Log component mount
  useEffect(() => {
    logger.appLifecycle('CONTACT_FORM_MOUNTED', {
      formFields: Object.keys(form)
    });
  }, [form]);

  const validate = () => {
    const newErrors = { ...initialErrors };
    let valid = true;
    const validationErrors = [];

    // First Name validation
    if (!form.fname.trim()) {
      newErrors.fname = "First name is required.";
      validationErrors.push({ field: 'fname', error: 'required' });
      valid = false;
    } else if (form.fname.trim().length < 2) {
      newErrors.fname = "First name must be at least 2 characters.";
      validationErrors.push({ field: 'fname', error: 'minLength' });
      valid = false;
    } else if (form.fname.trim().length > 50) {
      newErrors.fname = "First name must be less than 50 characters.";
      validationErrors.push({ field: 'fname', error: 'maxLength' });
      valid = false;
    } else if (!/^[a-zA-Z\s'-]+$/.test(form.fname.trim())) {
      newErrors.fname = "First name can only contain letters, spaces, hyphens, and apostrophes.";
      validationErrors.push({ field: 'fname', error: 'pattern' });
      valid = false;
    }

    // Last Name validation
    if (!form.lname.trim()) {
      newErrors.lname = "Last name is required.";
      validationErrors.push({ field: 'lname', error: 'required' });
      valid = false;
    } else if (form.lname.trim().length < 2) {
      newErrors.lname = "Last name must be at least 2 characters.";
      validationErrors.push({ field: 'lname', error: 'minLength' });
      valid = false;
    } else if (form.lname.trim().length > 50) {
      newErrors.lname = "Last name must be less than 50 characters.";
      validationErrors.push({ field: 'lname', error: 'maxLength' });
      valid = false;
    } else if (!/^[a-zA-Z\s'-]+$/.test(form.lname.trim())) {
      newErrors.lname = "Last name can only contain letters, spaces, hyphens, and apostrophes.";
      validationErrors.push({ field: 'lname', error: 'pattern' });
      valid = false;
    }

    // Email validation
    if (!form.email.trim()) {
      newErrors.email = "Email address is required.";
      validationErrors.push({ field: 'email', error: 'required' });
      valid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      newErrors.email = "Please enter a valid email address.";
      validationErrors.push({ field: 'email', error: 'format' });
      valid = false;
    } else if (form.email.trim().length > 100) {
      newErrors.email = "Email address must be less than 100 characters.";
      validationErrors.push({ field: 'email', error: 'maxLength' });
      valid = false;
    }

    // Service validation
    if (!form.service.trim()) {
      newErrors.service = "Please select a service of interest.";
      validationErrors.push({ field: 'service', error: 'required' });
      valid = false;
    }

    // Message validation
    if (!form.message.trim()) {
      newErrors.message = "Project description is required.";
      validationErrors.push({ field: 'message', error: 'required' });
      valid = false;
    } else if (form.message.trim().length < 10) {
      newErrors.message = "Please provide at least 10 characters describing your project.";
      validationErrors.push({ field: 'message', error: 'minLength' });
      valid = false;
    } else if (form.message.trim().length > 1000) {
      newErrors.message = "Project description must be less than 1000 characters.";
      validationErrors.push({ field: 'message', error: 'maxLength' });
      valid = false;
    }

    // Log validation results
    if (!valid) {
      logger.formValidationError('contact', validationErrors, {
        formData: {
          fname: form.fname,
          lname: form.lname,
          email: form.email,
          service: form.service,
          messageLength: form.message.length
        }
      });
    }

    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = async () => {
    logger.userInteraction('form_submit_attempt', 'contact_form', {
      hasData: !!form.fname && !!form.lname && !!form.email,
      rateLimited,
      cooldownTime
    });

    // Check if rate limited
    if (rateLimited || cooldownTime > 0) {
      const errorMessage = `Please wait ${retryAfter} seconds before submitting again.`;
      setSubmitError(errorMessage);
      logger.warn('FORM_SUBMIT_RATE_LIMITED', {
        retryAfter,
        cooldownTime,
        rateLimited
      });
      return;
    }

    if (validate()) {
      setLoading(true);
      setSubmitError("");
      setRateLimited(false);
      
      logger.formSubmission('contact', {
        formData: {
          name: `${form.fname} ${form.lname}`,
          email: form.email,
          company: form.service || "General Inquiry",
          messageLength: form.message.length
        }
      });
      
      try {
        // Prepare contact data for API
        const contactData = {
          name: `${form.fname} ${form.lname}`,
          email: form.email,
          company: form.service || "General Inquiry",
          message: form.message
        };

        // Submit to backend API
        const response = await apiService.submitContact(contactData);
        
        if (response.data.success) {
          logger.info('FORM_SUBMIT_SUCCESS', {
            requestId: response.data.requestId,
            email: form.email
          });
          setSubmitted(true);
          setForm(initialForm);
          setErrors(initialErrors);
        } else {
          logger.warn('FORM_SUBMIT_FAILED', {
            message: response.data.message,
            email: form.email
          });
          setSubmitError(response.data.message || "Failed to submit contact form. Please try again.");
        }
      } catch (error) {
        logger.error('FORM_SUBMIT_ERROR', {
          error: error.message,
          stack: error.stack,
          email: form.email,
          rateLimitError: error.message.includes("Rate limit exceeded"),
          validationError: !!error.validationErrors
        });
        
        // Handle rate limiting errors specifically
        if (error.message && error.message.includes("Rate limit exceeded")) {
          setRateLimited(true);
          // Extract retry after time from error if available
          const retryMatch = error.message.match(/wait (\d+) seconds/);
          const retrySeconds = retryMatch ? parseInt(retryMatch[1]) : 60;
          setRetryAfter(retrySeconds);
          setCooldownTime(retrySeconds);
          setSubmitError(`Rate limit exceeded. Please wait ${retrySeconds} seconds before trying again.`);
          
          logger.warn('FORM_SUBMIT_RATE_LIMIT_TRIGGERED', {
            retrySeconds,
            email: form.email
          });
          
          // Start cooldown countdown
          startCooldown(retrySeconds);
        }
        // Handle validation errors specifically
        else if (error.validationErrors) {
          // Map backend validation errors to frontend form fields
          const backendErrors = error.validationErrors;
          const newErrors = { ...initialErrors };
          
          // Map backend field names to frontend field names
          if (backendErrors.name) {
            // Backend "name" maps to frontend fname + lname
            newErrors.fname = backendErrors.name;
            newErrors.lname = backendErrors.name;
          }
          if (backendErrors.email) {
            newErrors.email = backendErrors.email;
          }
          if (backendErrors.company) {
            newErrors.service = backendErrors.company;
          }
          if (backendErrors.message) {
            newErrors.message = backendErrors.message;
          }
          
          logger.warn('FORM_SUBMIT_VALIDATION_ERROR', {
            backendErrors,
            email: form.email
          });
          
          setErrors(newErrors);
          setSubmitError("Please correct the errors in the form below.");
        } else {
          logger.error('FORM_SUBMIT_UNEXPECTED_ERROR', {
            error: error.message,
            email: form.email
          });
          setSubmitError(error.message || "An error occurred while submitting your message. Please try again.");
        }
      } finally {
        setLoading(false);
      }
    }
  };

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const inputStyle = (hasError) => ({
    borderColor: hasError ? "#f87171" : "rgba(45,125,255,.18)",
  });

  const startCooldown = (seconds) => {
    setCooldownTime(seconds);
    setRetryAfter(seconds);
  };

  // Cooldown countdown effect
  useEffect(() => {
    if (cooldownTime > 0) {
      const timer = setTimeout(() => {
        setCooldownTime(prev => prev - 1);
        setRetryAfter(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (rateLimited && cooldownTime === 0) {
      // Reset rate limit when cooldown ends
      setRateLimited(false);
      setRetryAfter(0);
    }
  }, [cooldownTime, rateLimited]);

  
  return (
    <section
      id="contact"
      className="section-padding"
      style={{ background: "var(--navy)" }}
      aria-labelledby="contact-title"
    >
      <div className="max-w-[1200px] mx-auto">
        <SectionHeader
          tag="Get In Touch"
          title="Start Your Transformation"
          subtitle="Tell us about your challenge. We'll respond within one business day."
        />

        <div
          className="grid gap-16 mt-12 items-start"
          style={{ gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))" }}
        >
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55 }}
          >
            <h3
              className="font-display font-bold text-white text-xl mb-4 tracking-tight"
              style={{ letterSpacing: "-0.02em" }}
            >
              Let's Build Something Great Together
            </h3>
            <p className="text-sm leading-8 mb-8" style={{ color: "var(--slate)" }}>
              Whether you're planning a cloud migration, building a data platform,
              or exploring AI, our experts are ready to scope your engagement.
            </p>

            <div className="space-y-5">
              {contactItems.map(({ icon, label, value }) => (
                <div key={label} className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-base flex-shrink-0"
                    style={{
                      background: "rgba(26,107,255,.12)",
                      border: "1px solid rgba(26,107,255,.2)",
                    }}
                    aria-hidden="true"
                  >
                    {icon}
                  </div>
                  <div>
                    <span
                      className="block text-xs uppercase tracking-wide"
                      style={{ color: "var(--slate)" }}
                    >
                      {label}
                    </span>
                    <strong className="text-sm font-normal" style={{ color: "var(--steel)" }}>
                      {value}
                    </strong>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, delay: 0.1 }}
            className="rounded-2xl p-8"
            style={{
              background: "rgba(15,31,61,.6)",
              border: "1px solid rgba(45,125,255,.15)",
            }}
            role="form"
            aria-label="Contact form"
          >
            {/* Name row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label
                  htmlFor="fname"
                  className="block text-xs font-medium uppercase tracking-wider mb-2"
                  style={{ color: "var(--slate)" }}
                >
                  First Name
                </label>
                <input
                  type="text"
                  id="fname"
                  placeholder="Jane"
                  value={form.fname}
                  onChange={handleChange("fname")}
                  style={inputStyle(errors.fname)}
                  aria-required="true"
                  aria-describedby={errors.fname ? "fname-err" : undefined}
                  autoComplete="given-name"
                />
                {errors.fname && (
                  <p id="fname-err" className="text-xs mt-1" style={{ color: "#f87171" }} role="alert">
                    {errors.fname}
                  </p>
                )}
              </div>
              <div>
                <label
                  htmlFor="lname"
                  className="block text-xs font-medium uppercase tracking-wider mb-2"
                  style={{ color: "var(--slate)" }}
                >
                  Last Name
                </label>
                <input
                  type="text"
                  id="lname"
                  placeholder="Smith"
                  value={form.lname}
                  onChange={handleChange("lname")}
                  style={inputStyle(errors.lname)}
                  aria-required="true"
                  aria-describedby={errors.lname ? "lname-err" : undefined}
                  autoComplete="family-name"
                />
                {errors.lname && (
                  <p id="lname-err" className="text-xs mt-1" style={{ color: "#f87171" }} role="alert">
                    {errors.lname}
                  </p>
                )}
              </div>
            </div>

            {/* Email */}
            <div className="mb-4">
              <label
                htmlFor="email"
                className="block text-xs font-medium uppercase tracking-wider mb-2"
                style={{ color: "var(--slate)" }}
              >
                Work Email
              </label>
              <input
                type="email"
                id="email"
                placeholder="jane@company.com"
                value={form.email}
                onChange={handleChange("email")}
                style={inputStyle(errors.email)}
                aria-required="true"
                aria-describedby={errors.email ? "email-err" : undefined}
                autoComplete="email"
              />
              {errors.email && (
                <p id="email-err" className="text-xs mt-1" style={{ color: "#f87171" }} role="alert">
                  {errors.email}
                </p>
              )}
            </div>

            {/* Service */}
            <div className="mb-4">
              <label
                htmlFor="service"
                className="block text-xs font-medium uppercase tracking-wider mb-2"
                style={{ color: "var(--slate)" }}
              >
                Service of Interest
              </label>
              <select
                id="service"
                value={form.service}
                onChange={handleChange("service")}
                style={inputStyle(errors.service)}
                aria-label="Select service of interest"
                aria-describedby={errors.service ? "service-err" : undefined}
              >
                <option value="" disabled>Select a service...</option>
                {serviceOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
              {errors.service && (
                <p id="service-err" className="text-xs mt-1" style={{ color: "#f87171" }} role="alert">
                  {errors.service}
                </p>
              )}
            </div>

            {/* Message */}
            <div className="mb-6">
              <label
                htmlFor="message"
                className="block text-xs font-medium uppercase tracking-wider mb-2"
                style={{ color: "var(--slate)" }}
              >
                Tell Us About Your Project
              </label>
              <textarea
                id="message"
                placeholder="Describe your challenge, timeline, or goals..."
                value={form.message}
                onChange={handleChange("message")}
                style={inputStyle(errors.message)}
                aria-required="true"
                aria-describedby={errors.message ? "msg-err" : undefined}
              />
              {errors.message && (
                <p id="msg-err" className="text-xs mt-1" style={{ color: "#f87171" }} role="alert">
                  {errors.message}
                </p>
              )}
            </div>

            <button
              className="btn-primary w-full text-base"
              style={{ 
                padding: ".9rem",
                opacity: (loading || rateLimited || cooldownTime > 0) ? 0.7 : 1,
                cursor: (loading || rateLimited || cooldownTime > 0) ? "not-allowed" : "pointer"
              }}
              onClick={handleSubmit}
              type="button"
              disabled={loading || rateLimited || cooldownTime > 0}
              aria-label="Submit contact form"
            >
              {loading ? "Sending..." : 
               rateLimited || cooldownTime > 0 ? 
                 `Please wait ${cooldownTime}s...` : 
                 "Send Message →"}
            </button>

            {submitError && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 rounded-lg px-5 py-4 text-sm"
                style={{
                  background: "rgba(248,113,113,.12)",
                  border: "1px solid rgba(248,113,113,.25)",
                  color: "#f87171",
                }}
                role="alert"
              >
                ⚠ {submitError}
              </motion.div>
            )}

            {submitted && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 rounded-lg px-5 py-4 text-sm"
                style={{
                  background: "rgba(0,160,100,.12)",
                  border: "1px solid rgba(0,160,100,.25)",
                  color: "#5de0c0",
                }}
                role="alert"
              >
                ✓ Thank you! We'll be in touch within one business day.
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
